import { Directive, ElementRef, HostListener, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Input numérico con separador de miles formateado EN VIVO mientras se
 * escribe (ej. al teclear "12345" el campo ya muestra "12,345"), no solo
 * al perder el foco. Solo permite dígitos y un punto decimal -- cualquier
 * otro carácter se descarta al escribir. El valor que llega al FormControl
 * siempre es un number (o null), nunca el string formateado.
 */
@Directive({
    selector: '[appThousandsFormat]',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => ThousandsFormatDirective),
        multi: true,
    }],
})
export class ThousandsFormatDirective implements ControlValueAccessor {
    private onChange: (value: number | null) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) { }

    writeValue(value: number | string | null): void {
        const num = this.toNumber(value);
        this.renderer.setProperty(this.el.nativeElement, 'value', num === null ? '' : this.format(String(num)));
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
    }

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
    }

    @HostListener('input', ['$event.target'])
    onInput(target: HTMLInputElement): void {
        const cursorFromEnd = target.value.length - (target.selectionStart ?? target.value.length);
        const digitsBeforeCursor = target.value.slice(0, target.value.length - cursorFromEnd).replace(/[^\d.]/g, '').length;

        let raw = target.value.replace(/[^\d.]/g, '');
        const parts = raw.split('.');
        if (parts.length > 2) {
            raw = parts[0] + '.' + parts.slice(1).join('');
        }

        const formatted = this.format(raw);
        target.value = formatted;

        let seen = 0;
        let newPos = formatted.length;
        for (let i = 0; i < formatted.length; i++) {
            if (/[\d.]/.test(formatted[i])) {
                seen++;
            }
            if (seen === digitsBeforeCursor) {
                newPos = i + 1;
                break;
            }
        }
        target.setSelectionRange(newPos, newPos);

        this.onChange(this.toNumber(raw));
    }

    /** "12345.6" -> "12,345.6"; "12345." -> "12,345."; deja el punto colgante mientras se sigue escribiendo. */
    private format(raw: string): string {
        if (raw === '' || raw === '.') return raw;
        const [intPart, decPart] = raw.split('.');
        const formattedInt = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
    }

    private toNumber(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') return null;
        const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
        return isNaN(num) ? null : num;
    }
}
